import { Injectable } from '@angular/core';
import {
    AuthClientConfig,
    AuthConfig,
    AuthService,
    IdToken,
} from '@auth0/auth0-angular';
import { catchError, Observable, of } from 'rxjs';
import axios from 'axios';
import { TokenStorageService } from 'viewbid-shared-lib';

/**
 * TokenService manages authentication tokens, checks their validity, and handles user logout on expiration.
 * NOTE:
 * - Auth0 provides us with a Refresh Token, ID Token and Access Token.
 * - As long as we have a valid Refresh Token we can always renew ID & Access Token.
 * - The only time is when we cannot get a valid/renew ID & Access Token is when the Refresh Token itself has expired.
 */
@Injectable({
    providedIn: 'root',
})
export class TokenService {
    private inProgress: boolean = false;

    constructor(
        private auth0: AuthService,
        private auth0Config: AuthClientConfig,
        private tokenStorageService: TokenStorageService,
    ) {}

    /**
     * Initiates a check on the ID Token's expiration and sets a logout timeout if necessary.
     * @param idToken The ID Token to be checked.
     */
    checkTokenAndSetTimeout(idToken: IdToken | null | undefined): void {
        if (!idToken || !idToken.exp) return;
        if (
            this.tokenStorageService.isMasquerading() &&
            window.name === 'masquerade'
        ) {
            return;
        }

        const expTime: number | undefined = idToken.exp * 1000;
        const timeoutDuration: number | undefined = expTime - Date.now();

        if (this.inProgress) return;
        if (timeoutDuration <= 0) {
            this.logout();
        }

        this.setLogoutTimeout(timeoutDuration);
    }

    /**
     * Sets a timeout to log the user out upon token expiration and checks for token updates.
     * @param duration Duration until the token expires.
     * @param currentToken The current ID Token.
     */
    private setLogoutTimeout(duration: number): void {
        this.inProgress = true;
        this.clearTokenRefreshTimeout();
        const timeoutId: ReturnType<typeof setTimeout> = setTimeout(() => {
            try {
                this.auth0
                    .getAccessTokenSilently()
                    .pipe(
                        catchError((error: any) => {
                            if (
                                error.error === 'login_required' ||
                                error.error === 'invalid_grant'
                            ) {
                                // Handle the error, force logout, or redirect to login
                                this.logout();
                                return of(null); // Prevent further execution or return a fallback value
                            }
                            throw error;
                        }),
                    )
                    .subscribe(() => {
                        this.auth0.idTokenClaims$.subscribe(
                            (updatedToken: IdToken | null | undefined) => {
                                this.handleUpdatedToken(updatedToken);
                            },
                        );
                    });
            } catch (error) {
                this.logout();
            }
        }, duration);

        localStorage.setItem('tokenRefreshTimeout', timeoutId.toString());
        this.inProgress = false;
    }

    /**
     * Handles the updated ID Token after the initial timeout.
     * @param updatedToken The updated ID Token.
     */
    private handleUpdatedToken(updatedToken: IdToken | null | undefined): void {
        if (
            updatedToken &&
            updatedToken.exp &&
            updatedToken.exp * 1000 <= Date.now() - 5000
        ) {
            this.inProgress = false;
            this.logout();
        } else {
            this.inProgress = false;
            this.checkTokenAndSetTimeout(updatedToken);
        }
    }

    /**
     * Clears any existing logout timeout.
     */
    clearTokenRefreshTimeout(): void {
        const timeoutId: number | null = this.getTimeoutIdFromLocalStorage();
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
            localStorage.removeItem('tokenRefreshTimeout');
        }
    }

    /**
     * Retrieves the logout timeout ID from local storage.
     * @returns The timeout ID or null if not found.
     */
    getTimeoutIdFromLocalStorage(): number | null {
        const timeoutId: string | null = localStorage.getItem(
            'tokenRefreshTimeout',
        );
        return timeoutId !== null ? Number(timeoutId) : null;
    }

    /**
     * Obtains a fresh access token silently from Auth0.
     */
    getAuth0AccessTokenSilently(): Observable<string> {
        return this.auth0.getAccessTokenSilently();
    }

    /**
     * Initiates the logout process by clearing any token refresh timeouts and using Auth0's logout method to redirect the user to the login page.
     */
    logout(): void {
        this.clearTokenRefreshTimeout();

        const config: AuthConfig = this.auth0Config.get();
        // eslint-disable-next-line
        const auth0Token = this.tokenStorageService.getLocalStorageItem(
            `@@auth0spajs@@::${config.clientId}::${config.authorizationParams?.['audience']}::${config.authorizationParams?.['scope']}`,
        );

        // eslint-disable-next-line
        const options = {
            method: 'POST',
            url: `https://${config.domain}/oauth/revoke`,
            headers: { 'content-type': 'application/json' },
            data: {
                client_id: config.clientId,
                token: auth0Token?.body?.refresh_token,
            },
        };

        // Clear app-specific tokens only
        this.tokenStorageService.clearAppStorage();

        axios.request(options).finally(() => {
            this.auth0.logout({
                logoutParams: {
                    returnTo: window.location.origin + '/login',
                },
            });
        });
    }
}
